import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, OptionChip, OptionTile } from '../components/ui'
import { ANSWER_EMOJI } from '../funnel/art'
import type { QuestionNode } from '../funnel/flow'
import { useFunnel } from '../funnel/useFunnel'
import { eventOnce, funnelApi, initialSessionId, type Answer } from '../lib/api'

/** Buckets, not raw milliseconds — enough to spot a screen that makes people stall. */
const DURATION_BUCKETS = [5, 10, 20, 30, 60, 120]
function bucketFor(ms: number): number {
  const seconds = ms / 1000
  return DURATION_BUCKETS.find((bound) => seconds <= bound) ?? 999
}

export function QuestionScreen({ node, isFirst }: { node: QuestionNode; isFirst?: boolean }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { answers, answerAndAdvance, step } = useFunnel()
  const startedAt = useRef(Date.now())

  const options = useMemo(() => node.optionsFor(answers), [node, answers])
  const existing = answers[node.questionKey]
  const [selection, setSelection] = useState<string[]>(
    Array.isArray(existing) ? existing : typeof existing === 'string' ? [existing] : []
  )

  useEffect(() => {
    startedAt.current = Date.now()
    void eventOnce(initialSessionId, 'step_viewed', { step: node.id, nodeId: node.id }).catch(() => {})
  }, [node.id])

  const save = (answer: Answer) => {
    // The first question is where the branch is decided.
    if (isFirst) {
      void funnelApi.event(initialSessionId, 'branch_assigned', { branch: String(answer) }).catch(() => {})
    }
    void funnelApi
      .event(initialSessionId, 'answer_saved', {
        questionKey: node.questionKey,
        durationBucket: bucketFor(Date.now() - startedAt.current),
      })
      .catch(() => {})
    answerAndAdvance(node.id, node.questionKey, answer)
  }

  // The age gate is the only place an answer can end the funnel. Everything
  // below it in the flow assumes a confirmed adult, so this is a hard stop.
  const pickSingle = (option: string) => {
    if (node.questionKey === 'age_band' && option === 'under_18') {
      void funnelApi.event(initialSessionId, 'adult_ineligible', {}).catch(() => {})
      void funnelApi.answer(initialSessionId, 'age_band', 'under_18').catch(() => {})
      navigate('/not-eligible', { replace: true })
      return
    }
    save(node.coerce ? node.coerce(option) : option)
  }

  const toggleMulti = (option: string) => {
    setSelection((current) => {
      // The exclusive option ("prefer not to say") clears everything else, and
      // any other choice clears it — the two states are contradictory.
      if (option === node.exclusiveOption) return current.includes(option) ? [] : [option]
      const without = current.filter((value) => value !== node.exclusiveOption)
      return without.includes(option) ? without.filter((value) => value !== option) : [...without, option]
    })
  }

  const copy = t(`questions.${node.id}`, { returnObjects: true }) as {
    title: string
    body?: string
    done?: string
    options: Record<string, string>
  }
  const { n, total } = step(node.id)

  return (
    <div className="content-column">
      {n > 0 ? <p className="eyebrow">{t('stepOf', { n, total })}</p> : null}
      <h1 className="question-title">{copy.title}</h1>
      {copy.body ? <p className="body-copy">{copy.body}</p> : null}

      {node.select === 'grid' ? (
        <div className="option-grid">
          {options.map((option) => (
            <OptionTile
              key={option}
              label={copy.options[option] ?? option}
              emoji={ANSWER_EMOJI[option]}
              quiet={option === 'under_18'}
              selected={existing === option}
              onClick={() => pickSingle(option)}
            />
          ))}
        </div>
      ) : (
        <div className="option-list">
          {options.map((option) => (
            <OptionChip
              key={option}
              label={`${ANSWER_EMOJI[option] ? `${ANSWER_EMOJI[option]}  ` : ''}${copy.options[option] ?? option}`}
              multi={node.select === 'multi'}
              selected={node.select === 'multi' ? selection.includes(option) : existing === option}
              onClick={() => (node.select === 'multi' ? toggleMulti(option) : pickSingle(option))}
            />
          ))}
        </div>
      )}

      {node.select === 'multi' ? (
        <Button block size="lg" variant="sunshine" onClick={() => save(selection)}>
          {selection.length ? t('continue') : (copy.done ?? t('skip'))}
        </Button>
      ) : null}

    </div>
  )
}
